import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import Kulturio from '@ekultur/kulturio-bridge';

/*
 * Restarts the unattended autoplay video when it pauses unexpectedly. On kiosks, ending a Sunshine/Moonlight
 * screen-sharing session can stop the video (and sound) until someone taps the screen. Why it pauses is not known,
 * so this recovers from the pause rather than fixing a cause. Field tested on a kiosk (playback resumes after
 * about 0.5 s).
 *
 * Only for the autoplay view, which has no pause button: any pause there is unintended. A few play() attempts
 * are made per pause (and at most four per 30 seconds), without seeking or reloading. A browser refusal, a new
 * source, the end of the media, an error or unmounting stops the recovery.
 */

const INITIAL_DELAY = 150;
const RETRY_DELAYS = [750, 1250, 2000, 3000];
const MAX_ATTEMPTS = RETRY_DELAYS.length;
const BUDGET_WINDOW = 30_000;
const LOG_INTERVAL = 5_000;

/** Write to the app log (desktop/BrightSign), or the console in a browser. Repeated events are logged once per 5 s */
const createLogger = (video: HTMLVideoElement) => {
  const lastLogged = new Map<string, number>();
  return (event: string, extra: Record<string, unknown> = {}) => {
    const now = performance.now();
    if (now - (lastLogged.get(event) ?? -Infinity) < LOG_INTERVAL) { return; }
    lastLogged.set(event, now);
    const state = { paused: video.paused, time: Math.round(video.currentTime * 100) / 100, readyState: video.readyState, errorCode: video.error?.code ?? null, ...extra };
    const message = `[Autoplay recovery] ${event} ${JSON.stringify(state)}`;
    try {
      (Kulturio.inDesktopApp() || Kulturio.inBrightSignApp()) ? Kulturio.log(message, "info") : console.info(message);
    } catch { /* Logging must never affect playback */ }
  };
}

/**
 * Bind recovery to a video element
 * @returns {function} Cleanup that removes the listeners and stops pending attempts
 */
export const attachAutoplayRecovery = (video: HTMLVideoElement): (() => void) => {
  const log = createLogger(video);
  const source = () => video.currentSrc || video.src;
  // The current recovery episode (one per pause), and the attempts used in the current 30 s window
  let episode: { source: string, attempts: number, timer?: ReturnType<typeof setTimeout> } | null = null;
  let budget = { start: performance.now(), used: 0 };
  let disposed = false;

  const canPlay = (current: NonNullable<typeof episode>) => !disposed && video.isConnected && video.autoplay && !video.controls
    && !video.ended && !video.error && Boolean(current.source) && source() === current.source;

  const finish = (reason?: string) => {
    if (!episode) { return; }
    clearTimeout(episode.timer);
    reason && log(reason, { attempts: episode.attempts });
    episode = null;
  };

  const attempt = (current: NonNullable<typeof episode>) => {
    if (episode !== current) { return; }
    if (!canPlay(current)) { return finish("cancelled"); }
    if (!video.paused) { return finish("resumed"); }

    const now = performance.now();
    if (now - budget.start >= BUDGET_WINDOW) { budget = { start: now, used: 0 }; }
    if (current.attempts >= MAX_ATTEMPTS || budget.used >= MAX_ATTEMPTS) { return finish("retry-limit"); }

    current.attempts++;
    budget.used++;
    log("requesting-play", { attempt: current.attempts });

    // A refusal by the browser is respected; other errors (e.g. AbortError) may use a later attempt
    const rejected = (error: any) => {
      if (episode !== current) { return; }
      const name = String(error?.name || "Error").slice(0, 80);
      log("play-rejected", { errorName: name, attempt: current.attempts });
      (name === "NotAllowedError" || name === "NotSupportedError") && finish("browser-refused-resume");
    };
    try {
      Promise.resolve(video.play()).then(() => episode === current && canPlay(current) && !video.paused && finish("resumed"), rejected);
    } catch (error) {
      rejected(error);
    }
    if (episode === current) { current.timer = setTimeout(() => attempt(current), RETRY_DELAYS[current.attempts - 1]); }
  };

  const listeners: [string, () => void][] = [
    ["pause", () => {
      log("pause");
      const current: NonNullable<typeof episode> = { source: source(), attempts: 0 };
      if (episode || !canPlay(current)) { return; }
      episode = current;
      current.timer = setTimeout(() => attempt(current), INITIAL_DELAY);
    }],
    ["playing", () => episode ? finish("resumed") : log("playing")],
    ["waiting", () => log("waiting")],
    ["stalled", () => log("stalled")],
    ...["emptied", "ended", "error"].map(name => [name, () => { log(name); finish(); }] as [string, () => void])
  ];
  listeners.forEach(([name, handler]) => video.addEventListener(name, handler));

  return () => {
    disposed = true;
    finish();
    listeners.forEach(([name, handler]) => video.removeEventListener(name, handler));
  };
}

/**
 * Restart an autoplay video that pauses unexpectedly (see above)
 * @param {object} videoRef The view's existing video ref, kept up to date
 * @returns {function} Callback ref for the video element
 */
const useAutoplayRecovery = (videoRef: MutableRefObject<HTMLVideoElement | null>) => {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const ref = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    setVideo(element);
  }, [videoRef]);

  useEffect(() => video ? attachAutoplayRecovery(video) : undefined, [video]);

  return ref;
}

export default useAutoplayRecovery;
